import React, { useEffect, useMemo } from "react";
import { useMutation } from "react-query";
import {
  ButtonPrimaryV2,
  UI_BODY,
  UI_BODY_SEMIBOLD_SM,
  UI_CAPTION,
  colors2,
  SkeletonLoader,
} from "oolib";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getAnnoMode } from "@/utils/getAnnoMode";
import { useGetAnnotationStatusOverview, getAnnotationCoverage } from "./hooks/useGetAnnotationStatusOverview";
import { useSocketContext } from "@/contexts/socketioContext";
import { useTriggerAIAutoAnnotate } from "@/utils/react-query-hooks/aiAnnotation";

/**
 * `testMode` (optional): forces the widget into a specific UI state without
 * needing a real auto-annotate run. Just type it directly on the mount.
 *
 *   <AIAnnotationWidget testMode={{ applyStatus: "loading" }} />              // "Queuing..."
 *   <AIAnnotationWidget testMode={{ isAnyContentTypeAnnotating: true }} />    // "Annotating..."
 *   <AIAnnotationWidget testMode={{ applyStatus: "error", applyError: {
 *     message: "something exploded"
 *   }}} />                                                                    // error block
 */
export const AIAnnotationWidget = ({ testMode } = {}) => {
  // Only show widget when the tenant is on the non-legacy annotation mode
  // AND AI annotation is switched on (the BE aiAnnotationEnabled middleware
  // rejects the trigger otherwise).
  const platformConfigs = useGetQueryData("platformConfigs");
  const hasNewTopicModellingConfig = getAnnoMode(platformConfigs) !== "legacy";
  const aiAnnotationEnabled = platformConfigs?.ai?.aiAnnotation?.enable === true;
  const showWidget = hasNewTopicModellingConfig && aiAnnotationEnabled;
  const { data: overview, status: overviewStatus } = useGetAnnotationStatusOverview({
    enabled: showWidget,
  });

  // Inline error block below handles error UX; suppress the hook's banner.
  const { mutateAsync: triggerAIAutoAnnotate } = useTriggerAIAutoAnnotate({
    silenceErrorBanner: true,
  });

  const {
    mutate: runAnnotation,
    reset: resetMutation,
    status: realApplyStatus,
    error: realApplyError,
  } = useMutation(async () => {
    // Fire one call per content type from the overview
    const contentTypes = (overview || []).map((ct) => ct.contentType);
    await Promise.all(
      contentTypes.map((ct) =>
        triggerAIAutoAnnotate({ contentType: ct, skipExisting: true })
      )
    );
  });

  // Doc-level coverage (published vs annotated), shared with the dashboard
  // "% Analysed" stat via getAnnotationCoverage.
  const stats = useMemo(() => getAnnotationCoverage(overview), [overview]);

  // Server-side in-flight detection: this widget runs auto-annotate across
  // every contentType in the overview, so we consider it "running" if any of
  // them is currently being annotated server-side. This survives page reloads
  // and other tabs (the lock is real, derived from the BullMQ queue state).
  const { autoAnnotateStatusByContentType } = useSocketContext() || {};
  const realIsAnyContentTypeAnnotating = useMemo(() => {
    if (!overview || !autoAnnotateStatusByContentType) return false;
    return overview.some(
      (ct) => autoAnnotateStatusByContentType[ct.contentType] === "autoAnnotating"
    );
  }, [overview, autoAnnotateStatusByContentType]);

  // Bridge the flicker between POST resolution and socket catching up:
  // once the server reports annotation in-flight, drop the FE-side "success"
  // status — `isAnyContentTypeAnnotating` now drives the UI alone.
  useEffect(() => {
    if (realIsAnyContentTypeAnnotating && realApplyStatus === "success") {
      resetMutation();
    }
  }, [realIsAnyContentTypeAnnotating, realApplyStatus, resetMutation]);

  // testMode overrides: each field falls back to the real value when not
  // provided so you can mock just one piece without rebuilding the rest.
  const applyStatus = testMode?.applyStatus ?? realApplyStatus;
  const applyError = testMode?.applyError ?? realApplyError;
  const isAnyContentTypeAnnotating =
    testMode?.isAnyContentTypeAnnotating ?? realIsAnyContentTypeAnnotating;

  // After the POST resolves but before the socket update lands (~700ms gap),
  // we don't yet have `isAnyContentTypeAnnotating === true` from the server,
  // but we KNOW we just successfully queued jobs. Treat this window as
  // "Annotating..." so the button doesn't flicker back to idle.
  const isAwaitingSocketConfirmation =
    applyStatus === "success" && !isAnyContentTypeAnnotating;

  const isError = applyStatus === "error";
  const isButtonDisabled =
    applyStatus === "loading" ||
    isAwaitingSocketConfirmation ||
    isAnyContentTypeAnnotating;

  const buttonLabel =
    applyStatus === "loading"
      ? "Queuing..."
      : isAwaitingSocketConfirmation || isAnyContentTypeAnnotating
      ? "Annotating..."
      : isError
      ? "Try Again"
      : "Annotate Now";

  const buttonIcon = isError ? "ArrowClockwise" : "Lightning";

  const errorMessage =
    applyError?.response?.data?.message ||
    applyError?.message ||
    "Couldn't reach the server.";

  if (!showWidget) return null;

  if (overviewStatus === "loading") {
    return (
      <div style={{ padding: "1.25rem", margin: "1rem 0" }}>
        <SkeletonLoader style={{ height: "80px", backgroundColor: colors2.grey5 }} />
      </div>
    );
  }

  if (!stats || stats.totalPublished === 0) return null;

  if (stats.unannotated === 0) {
    return (
      <div
        style={{
          padding: "1.25rem 1.5rem",
          margin: "1rem 0",
          background: colors2.green2,
          border: `1px solid ${colors2.green10}`,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <UI_BODY color="green80">
          All {stats.totalPublished} documents across {stats.datasetCount} dataset{stats.datasetCount !== 1 ? "s" : ""} have been annotated.
        </UI_BODY>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "1.25rem 1.5rem",
        margin: "1rem 0",
        background: colors2.white,
        border: `1px solid ${colors2.grey10}`,
        borderRadius: "8px",
      }}
    >
      {/* Action row. There's no separate "success" UI: once the POST
          resolves, the socket flips isAnyContentTypeAnnotating to true and
          the button label reflects that. The floating progress card is the
          richer feedback surface. */}
      <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
        <div style={{ flex: 1 }}>
          <UI_BODY>
            <strong>{stats.unannotated}</strong> document{stats.unannotated !== 1 ? "s" : ""} across{" "}
            {stats.datasetCount} dataset{stats.datasetCount !== 1 ? "s" : ""} have not been annotated yet.
          </UI_BODY>
          <UI_CAPTION color="grey50" style={{ marginTop: "0.25rem" }}>
            {stats.totalAnnotated} of {stats.totalPublished} already annotated
            {" "}({stats.percent}%)
          </UI_CAPTION>
        </div>
        <ButtonPrimaryV2
          icon={buttonIcon}
          onClick={() => runAnnotation()}
          disabled={isButtonDisabled}
        >
          {buttonLabel}
        </ButtonPrimaryV2>
      </div>

      {/* Inline error block — only shown after a failed click. Sits below
          the action row so the description (what the user was trying to do)
          stays visible above. The button morphs to "Try Again" on its own. */}
      {isError && (
        <div
          style={{
            marginTop: "1rem",
            padding: "0.75rem 1rem",
            background: colors2.redBG,
            border: `1px solid ${colors2.redText}`,
            borderRadius: "6px",
          }}
        >
          <UI_BODY_SEMIBOLD_SM style={{ marginBottom: "0.25rem", color: colors2.redText }}>
            Couldn't queue annotation
          </UI_BODY_SEMIBOLD_SM>
          <UI_CAPTION style={{color: colors2.redText}}>{errorMessage}</UI_CAPTION>
        </div>
      )}
    </div>
  );
};
